import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { trackEvent } from '../../services/analytics';
import {
  useAccountGroups,
  useApplyAccountGroupFlashBuy,
  useDiceDropLink,
  useDropConnections,
  useSetAllDropConnectionsEnabled,
  useSetAllDropConnectionsTier,
  useSetDropConnectionEnabled,
  useSetDropConnectionTier,
} from '../../hooks/useApi';
import { AccountGroup, AccountGroupFlashBuyResult } from '../../types';
import { AccountGroupChip, eligibilityLabel } from '../components/AccountGroupChip';
import { Badge, type BadgeTone } from '../components/Badge';
import { EmptyState } from '../components/EmptyState';
import { FlashBuyPreflightModal } from '../components/FlashBuyPreflightModal';
import { assessPreflight, preflightIsClean, PreflightReport, PreflightSyncOutcome } from '../components/flashBuyPreflight';
import { PageHeader } from '../components/PageHeader';
import { PageShell } from '../components/PageShell';
import { Panel } from '../components/Panel';
import { Skeleton, SkeletonList } from '../components/Skeleton';
import { cx, FOCUS_RING } from '../components/styles';
import { v2Path } from '../nav';

/**
 * v2 port of `src/pages/DropsPage.tsx` (DESIGN_PLAN §3.7).
 *
 * The logic block below is copied verbatim from v1 — same hooks, same memos,
 * same handlers, same four `trackEvent` calls, same three error-only toasts,
 * including the unused `activeAvailable` memo, which is deliberately left in
 * place. Only the JSX and the classNames are new.
 *
 * Two things this layout fixes without changing behaviour (§3.7.3): the two
 * explanations that v1 hides in `title` attributes — why `Open DICE Drop` is
 * disabled, and why a row toggle is blocked — are now also rendered as visible
 * text. The `title`s stay, so nothing is taken away; touch users simply get the
 * information at all.
 */

const SpinnerIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={cx(className, 'animate-spin')} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const DropsScreen: React.FC = () => {
  const { showError, showSuccess } = useToast();

  const { data, isLoading, error } = useDropConnections();
  const toggleOne = useSetDropConnectionEnabled();
  const toggleAll = useSetAllDropConnectionsEnabled();
  const setTier = useSetDropConnectionTier();
  const setAllTiers = useSetAllDropConnectionsTier();
  const dropLink = useDiceDropLink();
  const { data: groups = [] } = useAccountGroups();
  const applyGroup = useApplyAccountGroupFlashBuy();

  useEffect(() => { trackEvent('drops_page_viewed'); }, []);

  // Memoised so the group counters below do not recompute on every render —
  // `data?.connections ?? []` yields a fresh array each time data is absent.
  const connections = useMemo(() => data?.connections ?? [], [data]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  /** How many of a group's members are currently switched on, from the rows. */
  const enabledInGroup = useMemo(() => {
    const counts = new Map<number, number>();
    for (const conn of connections) {
      if (!conn.dice_drop_enabled) continue;
      for (const id of conn.group_ids ?? []) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  }, [connections]);

  /**
   * One sentence per group action, naming what changed AND what was skipped —
   * a group action that quietly leaves out an AXS or banned account is exactly
   * the surprise this page must not produce.
   */
  const describeGroupResult = (res: AccountGroupFlashBuyResult, verb: string): string => {
    const parts: string[] = [];
    if (res.enabled !== null) {
      parts.push(res.applied === 0
        ? `${verb}: nothing to change`
        : `${verb} ${res.applied} account${res.applied !== 1 ? 's' : ''}`);
    }
    if (res.tier_match !== null) {
      const label = res.tier_match === 'match' ? 'Match Tier' : res.tier_match.toUpperCase();
      parts.push(`${res.tier_updated} set to ${label}`);
    }
    if (res.disabled_others > 0) {
      parts.push(`${res.disabled_others} outside the group switched off`);
    }
    parts.push(eligibilityLabel(res.eligible, res.members, res.skipped));
    return `${parts.join(' · ')}. Accounts added to the group later get the same.`;
  };

  const handleGroupEnable = async (group: AccountGroup, enabled: boolean, exclusive = false) => {
    try {
      const res = await applyGroup.mutateAsync({ id: group.id, payload: { enabled, exclusive } });
      trackEvent('drop_group_toggle', { enabled, exclusive, group_id: group.id, applied: res.applied });
      showSuccess(`${group.name} — ${describeGroupResult(res, enabled ? (exclusive ? 'Enabled only' : 'Enabled') : 'Disabled')}`);
    } catch (err: any) {
      showError(err?.message || 'Failed to update the group');
    }
  };

  const handleGroupTier = async (group: AccountGroup, value: string) => {
    if (!value) return;
    const tier = value as 'ga' | 'vip' | 'match';
    try {
      const res = await applyGroup.mutateAsync({ id: group.id, payload: { tier_match: tier } });
      trackEvent('drop_group_tier_set', { tier, group_id: group.id, count: res.tier_updated });
      showSuccess(`${group.name} — ${describeGroupResult(res, 'Tier')}`);
    } catch (err: any) {
      showError(err?.message || 'Failed to update the group tier');
    }
  };
  const enabledCount = useMemo(
    () => connections.filter((c) => c.dice_drop_enabled).length,
    [connections]
  );
  const activeAvailable = useMemo(
    () => connections.filter((c) => c.is_active && !c.is_banned).length,
    [connections]
  );
  const allEnabled = connections.length > 0 && enabledCount === connections.length;
  const noneEnabled = enabledCount === 0;

  /**
   * Pre-flight (see `flashBuyPreflight.ts`): opening the drop tool does not
   * refresh any DICE token, so enabled accounts that have not been synced
   * recently show up there as "Token Expired" and get reported as a bug. When
   * any enabled account is stale or needs reconnecting, ask first; a seller
   * who just synced goes straight through.
   */
  const [preflight, setPreflight] = useState<PreflightReport | null>(null);

  const mintAndOpenDrop = async () => {
    trackEvent('dice_drop_open_clicked', { enabled_count: enabledCount });
    try {
      const { url } = await dropLink.mutateAsync();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      showError(err?.message || 'Failed to open DICE drop. Please try again.');
    }
  };

  const handleOpenDrop = async () => {
    if (enabledCount === 0) return;
    const report = assessPreflight(connections);
    if (preflightIsClean(report)) {
      await mintAndOpenDrop();
      return;
    }
    trackEvent('dice_drop_preflight_shown', {
      stale: report.stale.length,
      needs_reconnect: report.needsReconnect.length,
      enabled_count: enabledCount,
    });
    setPreflight(report);
  };

  const handlePreflightOpen = () => {
    setPreflight(null);
    void mintAndOpenDrop();
  };

  const handlePreflightSynced = (outcome: PreflightSyncOutcome) => {
    trackEvent('dice_drop_preflight_sync', {
      succeeded: outcome.succeeded.length,
      failed: outcome.failed.length,
    });
  };

  const handleBulk = async (enabled: boolean) => {
    try {
      await toggleAll.mutateAsync(enabled);
      trackEvent('drop_bulk_toggle', { enabled });
    } catch (err: any) {
      showError(err?.message || 'Failed to update accounts');
    }
  };

  /**
   * Set one ticket tier across every enabled account at once.
   *
   * Enabled accounts only, matching the server: the per-row picker is hidden on
   * disabled rows, and a bulk action must never write a value the user cannot
   * then see or undo from the same screen.
   *
   * The select resets to its placeholder afterwards rather than holding the
   * chosen tier. It is an action, not a state — rows can still differ from each
   * other a moment later via the per-row pickers, so a select that kept showing
   * "VIP" would be claiming something it cannot guarantee.
   */
  const handleBulkTier = async (value: string) => {
    const tierMatch = value === 'match' ? null : value;
    try {
      const res = await setAllTiers.mutateAsync(tierMatch);
      trackEvent('drop_bulk_tier_set', {
        tier: tierMatch ?? 'default',
        count: res.tier_updated,
      });
      const label = tierMatch ? tierMatch.toUpperCase() : 'Match Tier';
      showSuccess(
        res.tier_updated === 0
          ? `Every enabled account was already set to ${label}.`
          : `Set ${res.tier_updated} account${res.tier_updated !== 1 ? 's' : ''} to ${label}.`,
      );
    } catch (err: any) {
      showError(err?.message || 'Failed to update ticket tier');
    }
  };

  const handleToggle = async (id: number, next: boolean) => {
    try {
      await toggleOne.mutateAsync({ id, enabled: next });
      trackEvent('drop_single_toggle', { enabled: next });
    } catch (err: any) {
      showError(err?.message || 'Failed to update account');
    }
  };

  const handleTier = async (id: number, value: string) => {
    // '' = follow the drop's Match Tier at fire time — the value typed into
    // "Match Tier (default)" in the Flash Buy tool. The option is labelled
    // "Match Tier" to name that field rather than say "Default" and leave the
    // seller guessing which default. Keep the analytics value below as
    // 'default' regardless: renaming it would split the drop_tier_set metric
    // from its own history.
    const tierMatch = value || null;
    try {
      await setTier.mutateAsync({ id, tierMatch });
      trackEvent('drop_tier_set', { tier: tierMatch ?? 'default' });
    } catch (err: any) {
      showError(err?.message || 'Failed to update tier');
    }
  };

  // Same string that already sits in the button's `title`, surfaced as text.
  const disabledReason = 'Enable at least one DICE account below to use the drop tool';

  return (
    <PageShell>
      <PageHeader
        title="Flash Buy"
        badge={<Badge tone="blue">Beta</Badge>}
        subtitle="Choose which DICE accounts are available to Flash Buy, and set each one's ticket tier"
        actions={
          <button
            onClick={handleOpenDrop}
            disabled={enabledCount === 0 || dropLink.isPending}
            title={
              enabledCount === 0
                ? 'Enable at least one DICE account below to use the drop tool'
                : `Opens the drop tool with ${enabledCount} enabled account${enabledCount !== 1 ? 's' : ''}`
            }
            className={cx(
              'flex items-center justify-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-xl text-sm font-semibold',
              'bg-rose-500/10 border border-rose-500/20 text-rose-300 hover:bg-rose-500/20 hover:border-rose-500/30 hover:text-rose-200 transition-all',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-rose-500/10 disabled:hover:border-rose-500/20 disabled:hover:text-rose-300',
              FOCUS_RING,
            )}
          >
            {dropLink.isPending ? (
              <SpinnerIcon />
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            )}
            <span>Open DICE Drop</span>
            <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 17L17 7M17 7H7M17 7V17" />
            </svg>
          </button>
        }
      />

      <div className="space-y-6">
        {/* Summary */}
        <Panel>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              {isLoading ? (
                <>
                  <Skeleton className="h-5 w-64 max-w-full" />
                  <p className="mt-2 text-sm text-white/40">
                    Only enabled accounts are exposed to the drop tool when you open it.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-base font-medium text-white">
                    {enabledCount} of {connections.length} DICE account{connections.length !== 1 ? 's' : ''} enabled for drop
                  </h2>
                  <p className="text-sm text-white/40 mt-1">
                    Only enabled accounts are exposed to the drop tool when you open it.
                  </p>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulk(true)}
                disabled={toggleAll.isPending || allEnabled || connections.length === 0}
                className={cx(
                  'flex-1 md:flex-none px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20 transition-all',
                  'disabled:opacity-30 disabled:cursor-not-allowed',
                  FOCUS_RING,
                )}
              >
                Enable all
              </button>
              <button
                onClick={() => handleBulk(false)}
                disabled={toggleAll.isPending || noneEnabled}
                className={cx(
                  'flex-1 md:flex-none px-4 py-2 rounded-lg text-xs font-semibold bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white/80 transition-all',
                  'disabled:opacity-30 disabled:cursor-not-allowed',
                  FOCUS_RING,
                )}
              >
                Disable all
              </button>
            </div>
          </div>

          {/* Bulk ticket type. Sits under the enable controls because it only
              does anything once accounts are enabled, and it is disabled with a
              stated reason when none are — rather than silently no-opping. */}
          <div className="mt-4 flex flex-col gap-2 border-t border-white/[0.06] pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm text-white">Set ticket type for all enabled accounts</p>
              <p className="mt-0.5 text-xs text-white/40">
                Overwrites every enabled account's tier. Change individual accounts below afterwards.
              </p>
            </div>
            <label className="flex flex-shrink-0 items-center gap-2">
              <span className="sr-only">Ticket type for all enabled accounts</span>
              <select
                value=""
                disabled={setAllTiers.isPending || noneEnabled || isLoading}
                onChange={(e) => { if (e.target.value) handleBulkTier(e.target.value); }}
                className={cx(
                  'rounded-lg border border-white/[0.10] bg-white/[0.04] px-3 py-2 text-xs font-semibold text-white/80',
                  'hover:border-white/20 focus:outline-none disabled:opacity-30 disabled:cursor-not-allowed',
                  FOCUS_RING,
                )}
              >
                <option value="" disabled>
                  {setAllTiers.isPending ? 'Applying…' : 'Apply to all…'}
                </option>
                <option value="match">Match Tier</option>
                <option value="ga">GA</option>
                <option value="vip">VIP</option>
              </select>
            </label>
          </div>

          {/* §3.7.3 — the `Open DICE Drop` tooltip, as readable text. */}
          {!isLoading && enabledCount === 0 && connections.length > 0 && (
            <p className="mt-4 text-xs text-amber-400/80">{disabledReason}.</p>
          )}
        </Panel>

        {/* Groups. Each action applies to the group's eligible DICE members
            now and becomes the group's standing rule, so an account tagged
            into the group next week is treated the same way. "Only this
            group" additionally switches off every DICE account outside it. */}
        {!isLoading && connections.length > 0 && (
          <Panel>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
              <div className="min-w-0">
                <h2 className="text-base font-medium text-white">Groups</h2>
                <p className="mt-1 text-sm text-white/40">
                  {groups.length > 0
                    ? 'Switch a whole group on or off, or set its tier. Applies now and to accounts added to the group later.'
                    : 'Tag accounts into groups on the Accounts page to switch them on and off together here.'}
                </p>
              </div>
              <Link
                to={v2Path('/accounts')}
                className={cx('flex-shrink-0 text-xs text-[#60a5fa] hover:underline', FOCUS_RING)}
              >
                {groups.length > 0 ? 'Edit groups' : 'Go to Accounts'}
              </Link>
            </div>

            {groups.length > 0 && (
              <ul className="mt-4 divide-y divide-white/[0.06]">
                {groups.map((group) => {
                  const on = enabledInGroup.get(group.id) ?? 0;
                  const eligible = group.eligible_flash_buy_count;
                  const busy = applyGroup.isPending;
                  return (
                    <li key={group.id} className="flex flex-col gap-3 py-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <AccountGroupChip group={group} size="md" />
                        <span className="text-xs text-white/50">
                          {eligibilityLabel(eligible, group.member_count, group.skipped)}
                        </span>
                        {eligible > 0 && (
                          <Badge tone={on === 0 ? 'neutral' : on === eligible ? 'green' : 'amber'} size="sm" dot>
                            {on} of {eligible} on
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleGroupEnable(group, true)}
                          disabled={busy || eligible === 0 || on === eligible}
                          title={eligible === 0 ? 'No eligible DICE accounts in this group' : 'Switch this group on'}
                          className={cx(
                            'rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 transition-all hover:bg-emerald-500/20',
                            'disabled:cursor-not-allowed disabled:opacity-30',
                            FOCUS_RING,
                          )}
                        >
                          Enable
                        </button>
                        <button
                          type="button"
                          onClick={() => handleGroupEnable(group, true, true)}
                          disabled={busy || eligible === 0}
                          title="Switch this group on and every other DICE account off"
                          className={cx(
                            'rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-300 transition-all hover:bg-rose-500/20',
                            'disabled:cursor-not-allowed disabled:opacity-30',
                            FOCUS_RING,
                          )}
                        >
                          Only this group
                        </button>
                        <button
                          type="button"
                          onClick={() => handleGroupEnable(group, false)}
                          disabled={busy || on === 0}
                          className={cx(
                            'rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/60 transition-all hover:bg-white/10 hover:text-white/80',
                            'disabled:cursor-not-allowed disabled:opacity-30',
                            FOCUS_RING,
                          )}
                        >
                          Disable
                        </button>
                        <label className="flex items-center gap-2">
                          <span className="sr-only">Ticket tier for {group.name}</span>
                          <select
                            value=""
                            disabled={busy || on === 0}
                            onChange={(e) => handleGroupTier(group, e.target.value)}
                            title={on === 0 ? 'Enable the group first — tiers only apply to enabled accounts' : 'Set the tier for every enabled account in this group'}
                            className={cx(
                              'rounded-lg border border-white/[0.10] bg-white/[0.04] px-2.5 py-1.5 text-xs font-semibold text-white/80',
                              'hover:border-white/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-30',
                              FOCUS_RING,
                            )}
                          >
                            <option value="" disabled>
                              {group.tier_match ? `Tier: ${group.tier_match === 'match' ? 'Match' : group.tier_match.toUpperCase()}` : 'Set tier…'}
                            </option>
                            <option value="match">Match Tier</option>
                            <option value="ga">GA</option>
                            <option value="vip">VIP</option>
                          </select>
                        </label>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        )}

        {/* Connections list */}
        {isLoading ? (
          <SkeletonList count={3} as="row" />
        ) : error ? (
          <EmptyState
            tone="error"
            title={error instanceof Error ? error.message : 'Failed to load accounts'}
          />
        ) : connections.length === 0 ? (
          <EmptyState
            title="No DICE accounts connected"
            body="Connect a DICE account before you can expose it to the drop tool."
            action={
              <Link
                to={v2Path('/accounts')}
                className={cx(
                  'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-rose-500/10 border border-rose-500/20 text-rose-300 hover:bg-rose-500/20 transition-all',
                  FOCUS_RING,
                )}
              >
                Go to Accounts
              </Link>
            }
          />
        ) : (
          <div className="space-y-2">
            {connections.map((conn) => {
              const blocked = !conn.is_active || conn.is_banned;
              const statusLabel = conn.is_banned
                ? 'Banned'
                : !conn.is_active
                  ? 'Inactive'
                  : conn.failed_refresh
                    ? 'Needs reauth'
                    : 'Active';
              const statusTone: BadgeTone = conn.is_banned
                ? 'red'
                : !conn.is_active
                  ? 'neutral'
                  : conn.failed_refresh
                    ? 'amber'
                    : 'green';

              return (
                <div
                  key={conn.id}
                  className={cx(
                    'flex items-center justify-between gap-4 p-4 sm:p-5 rounded-2xl border transition-all',
                    conn.dice_drop_enabled
                      ? 'bg-rose-500/[0.04] border-rose-500/20'
                      : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.10]',
                  )}
                >
                  <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                    <div className="w-12 h-12 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-rose-400">D</span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-medium text-white">DICE</h3>
                        <Badge tone={statusTone} size="sm" dot>
                          {statusLabel}
                        </Badge>
                      </div>
                      <p className="text-sm text-white/50 mt-0.5 truncate">
                        {conn.phone || conn.email || `Account ${conn.id}`}
                        {conn.email && conn.phone && (
                          <span className="text-white/30"> · {conn.email}</span>
                        )}
                      </p>
                      {/* §3.7.3 — the toggle's tooltip, as readable text. */}
                      {blocked && (
                        <p className="mt-1 text-xs text-white/35">
                          Account must be active to enable drop.
                        </p>
                      )}
                      {(conn.group_ids ?? []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {(conn.group_ids ?? []).map((id) => {
                            const g = groupById.get(id);
                            return g ? <AccountGroupChip key={id} group={g} /> : null;
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-3">
                    {/* Tier carried into the drop tool; still overridable there
                        by free text. Only meaningful for participating accounts.
                        "Match Tier" is not a tier — it defers to the drop-wide
                        value, so it stays first in the list. */}
                    {conn.dice_drop_enabled && (
                      <label className="flex items-center gap-2">
                        <span className="hidden text-xs text-white/40 sm:inline">Tier</span>
                        <select
                          value={conn.tier_match ?? ''}
                          disabled={setTier.isPending}
                          onChange={(e) => handleTier(conn.id, e.target.value)}
                          aria-label={`Ticket tier for ${conn.phone || conn.email || `account ${conn.id}`}`}
                          className={cx(
                            'rounded-lg border border-white/[0.10] bg-white/[0.04] px-2.5 py-1.5 text-xs font-medium text-white/80',
                            'hover:border-white/20 focus:outline-none disabled:opacity-50',
                            FOCUS_RING,
                          )}
                        >
                          <option value="">Match Tier</option>
                          <option value="ga">GA</option>
                          <option value="vip">VIP</option>
                        </select>
                      </label>
                    )}

                    <label
                      className={cx(
                        'relative inline-flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-end',
                        blocked && 'opacity-40 cursor-not-allowed',
                      )}
                      title={blocked ? 'Account must be active to enable drop' : ''}
                    >
                      <span className="sr-only">
                        Enable {conn.phone || conn.email || `account ${conn.id}`} for the drop tool
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={conn.dice_drop_enabled}
                        disabled={blocked || toggleOne.isPending}
                        onChange={(e) => handleToggle(conn.id, e.target.checked)}
                      />
                      <div className="relative w-11 h-6 bg-white/10 peer-focus:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-[#3b82f6]/60 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#0a0a0a] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white/20 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-500/60" />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {preflight && (
        <FlashBuyPreflightModal
          report={preflight}
          onOpen={handlePreflightOpen}
          onClose={() => setPreflight(null)}
          onSyncComplete={handlePreflightSynced}
          onOpenAnyway={() => trackEvent('dice_drop_preflight_open_anyway', {
            stale: preflight.stale.length,
            needs_reconnect: preflight.needsReconnect.length,
          })}
        />
      )}
    </PageShell>
  );
};
